// Timetable.js - Professional Redesign with Calendar Event Integration
import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { 
  Clock, 
  MapPin, 
  Users, 
  BookOpen, 
  Coffee, 
  PlayCircle,
  AlertCircle,
  User,
  FlaskConical,
  BookMarked,
  CalendarDays,
  LayoutGrid,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Layers,
  GraduationCap,
  Trophy,
  PartyPopper,
  Sun,
  XCircle,
  Info
} from 'lucide-react';
import api from '../../api';

const Timetable = ({ facultyId, tenantId, academicYearId, semesterType, onDateSelect, isModal = false }) => {
  const [weekStart, setWeekStart] = useState(moment().startOf('isoWeek'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [weekData, setWeekData] = useState(null);
  // const [facultyInfo, setFacultyInfo] = useState(null); // Removed unused state
  const [viewType, setViewType] = useState('week');
  const [selectedDate, setSelectedDate] = useState(null);
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [activeView] = useState('custom');
  const [hoveredCell, setHoveredCell] = useState(null);

  const fetchPersonalTimetable = useCallback(async (start, specificDate = null) => {
    if (!facultyId) {
      setError('Faculty ID is required');
      return;
    }

    setLoading(true);
    setError(null);

    const params = {
      weekStartDate: start.format('YYYY-MM-DD'),
      viewMode: activeView,
      upcomingView: true
    };

    if (specificDate) {
      params.specificDate = specificDate;
    }

    const url = `/api/faculty/${encodeURIComponent(facultyId)}/timetable/personal`;

    try {
      const resp = await api.get(url, { params });

      if (resp?.data?.success) {
        setWeekData(resp.data.data.week);
        setUpcomingClasses(resp.data.data.upcomingClasses || []);
      } else {
        const msg = resp?.data?.message || 'Failed to load personal timetable';
        setError(msg);
        setWeekData(null);
        setUpcomingClasses([]);
      }
    } catch (err) {
      console.error('Error fetching timetable:', err);
      setError(err.response?.data?.message || err.message || 'Server error');
      setWeekData(null);
      setUpcomingClasses([]);
    } finally {
      setLoading(false);
    }
  }, [facultyId, activeView]);

  useEffect(() => {
    fetchPersonalTimetable(weekStart, selectedDate);
  }, [fetchPersonalTimetable, weekStart, selectedDate]);

  const prevWeek = () => setWeekStart(s => moment(s).subtract(1, 'week'));
  const nextWeek = () => setWeekStart(s => moment(s).add(1, 'week'));
  const todayWeek = () => {
    setWeekStart(moment().startOf('isoWeek'));
    setSelectedDate(null);
  };

  const handleViewChange = (view) => {
    setViewType(view);
    if (view === 'master' || view === 'week') {
      setSelectedDate(null);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setViewType('day');
    if (onDateSelect) onDateSelect(date);
  };

  const getCurrentClass = () => {
    return upcomingClasses.find(cls => 
      cls.timeStatus === 'in_progress' || cls.timeStatus === 'starting_soon'
    );
  };

  const getNextClass = () => {
    const current = getCurrentClass();
    if (current) {
      return upcomingClasses.find(cls => 
        cls.timeStatus === 'upcoming' && cls.classTime !== current.classTime
      );
    }
    return upcomingClasses.find(cls => cls.timeStatus === 'upcoming');
  };

  const getSelectedDay = () => {
    if (!selectedDate || !weekData) return null;
    return weekData.find(day => day.dateISO === selectedDate);
  };

  // Helper to format time in 12-hour format
  const format12Hour = (timeStr) => {
    if (!timeStr) return '--:--';
    // Accepts 'HH:mm' or 'H:mm' or already with AM/PM
    const m = timeStr.match(/^(\d{1,2}):(\d{2})(?: ?([AP]M))?$/i);
    if (!m) return timeStr;
    let h = parseInt(m[1], 10);
    const min = m[2];
    let ampm = m[3];
    if (ampm) return `${h}:${min} ${ampm.toUpperCase()}`;
    ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    if (h === 0) h = 12;
    return `${h}:${min} ${ampm}`;
  };

  const getTimeDisplay = (timeSlot) => {
    if (!timeSlot) return '--:--';
    if (timeSlot.start && timeSlot.end) {
      return `${format12Hour(timeSlot.start)} - ${format12Hour(timeSlot.end)}`;
    }
    return '--:--';
  };

  const calculateTotalTeachingSessions = (entries, includeEvents = false) => {
    if (!entries) return 0;
    return entries.filter(e => {
      if (e.isBreak) return false;
      if (!includeEvents && e.isEvent) return false;
      if (e.type === 'Custom' && !e.isTeachingSession) return false;
      return e.isTeachingSession !== false;
    }).length;
  };

  const calculateTotalWeekSessions = () => {
    if (!weekData) return 0;
    return weekData.reduce((total, day) => total + calculateTotalTeachingSessions(day.entries, false), 0);
  };

  const isCurrentLiveSession = (entry) => {
    if (!entry || !upcomingClasses) return false;
    return upcomingClasses.some(cls =>
      (cls.timeStatus === 'in_progress' || cls.timeStatus === 'starting_soon') &&
      cls.subjectCode === entry.subjectCode &&
      cls.className === entry.className &&
      cls.timeSlot?.start === entry.timeSlot?.start &&
      cls.timeSlot?.end === entry.timeSlot?.end
    );
  };

  const getEventIcon = (eventType, eventCategory) => {
    const iconProps = { className: "w-4 h-4" };
    switch(eventType) {
      case 'holiday':
        return <Sun {...iconProps} className="text-orange-500" />;
      case 'exam':
        return <GraduationCap {...iconProps} className="text-red-500" />;
      case 'academic':
        if (eventCategory === 'cultural') return <PartyPopper {...iconProps} className="text-purple-500" />;
        if (eventCategory === 'sports') return <Trophy {...iconProps} className="text-green-500" />;
        return <BookOpen {...iconProps} className="text-blue-500" />;
      default:
        return <Info {...iconProps} className="text-gray-500" />;
    }
  };

  const shouldCancelClasses = (event) => {
    return event.eventType === 'holiday' || event.eventType === 'exam';
  };

  // Helper to get all non-cancelling events for a day
  const getNonCancellingEventsForDay = (dayData) => {
    if (!dayData || !dayData.entries) return [];
    return dayData.entries.filter(e => e.isEvent && !shouldCancelClasses(e));
  };

  // Helper to get cancelling events for a day
  const getCancellingEventsForDay = (dayData) => {
    if (!dayData || !dayData.entries) return [];
    return dayData.entries.filter(e => e.isEvent && shouldCancelClasses(e));
  };

  // renderEventBadges removed (was unused)

  const renderTeachingCell = (entry, isHovered = false) => {
    const isOverride = entry.isOverride;
    const isBreak = entry.isBreak;
    const isLab = entry.type === 'Lab' || entry.isLab;
    const batchName = entry.batchName || entry.labBatchName;
    const subjectCode = entry.subjectCode;
    
    if (isBreak) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-amber-700 py-1">
          <Coffee className="w-4 h-4 mb-1 text-amber-500/70" />
          <span className="text-xs font-semibold">{entry.label || 'Break'}</span>
          <span className="text-[10px] text-amber-500 font-medium mt-0.5">{getTimeDisplay(entry.timeSlot)}</span>
        </div>
      );
    }
    
    const highlightClass = isCurrentLiveSession(entry)
      ? 'ring-2 ring-indigo-500 ring-offset-1 bg-gradient-to-br from-indigo-50 to-indigo-100/50 shadow-md'
      : '';
    
    return (
      <div className={`flex flex-col items-center justify-center h-full text-xs p-2.5 relative transition-all duration-200 text-center w-full rounded-xl ${
        isOverride ? 'bg-gradient-to-br from-amber-50 via-white to-amber-50/30 border border-amber-200' : ''
      } ${isLab && !isCurrentLiveSession(entry) ? 'border-l-4 border-purple-500 bg-purple-50/30' : ''} ${isHovered ? 'bg-slate-50' : ''} ${highlightClass}`}>
        {isOverride && (
          <div className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-amber-500 rounded-full shadow-sm"></div>
        )}
        <div className="font-bold text-slate-800 text-sm mb-1 line-clamp-2 leading-tight tracking-tight">
          {entry.subjectName || entry.className || 'Session'}
        </div>
        {subjectCode && (
          <div className="flex items-center justify-center gap-1 text-indigo-600 font-mono text-[10px] font-semibold mb-0.5 bg-indigo-50 px-1.5 py-0.5 rounded-md">
            <BookMarked className="w-3 h-3 flex-shrink-0" />
            <span className="uppercase tracking-wide">{subjectCode}</span>
          </div>
        )}
        {entry.className && (
          <div className="text-emerald-700 text-[11px] font-bold mb-0.5 line-clamp-1">
            {entry.className}{entry.section ? ` (${entry.section})` : ''}
          </div>
        )}
        {isLab && batchName && (
          <div className="flex items-center justify-center gap-1 text-purple-700 font-medium text-[10px] mb-0.5 bg-purple-100/50 px-1.5 py-0.5 rounded-md">
            <FlaskConical className="w-3 h-3 flex-shrink-0" />
            <span className="line-clamp-1">{batchName}</span>
          </div>
        )}
        {entry.room && (
          <div className="flex items-center justify-center gap-1 text-slate-400 text-[10px] font-medium mt-0.5">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{entry.room}</span>
          </div>
        )}
      </div>
    );
  };

  const renderEventCell = (entry, isHovered = false) => {
    const isCancellingEvent = shouldCancelClasses(entry);
    return (
      <div className={`flex flex-col items-center justify-center h-full p-3 rounded-xl w-full max-w-md mx-auto shadow-sm transition-all ${
        isCancellingEvent ? 'bg-gradient-to-br from-red-50 to-white border border-red-200' : 'bg-gradient-to-br from-indigo-50 to-white border border-indigo-100'
      } ${isHovered ? 'transform scale-[1.01] shadow-md' : ''}`}>
        <div className="p-1.5 bg-white rounded-lg shadow-sm mb-1.5">
          {getEventIcon(entry.eventType, entry.eventCategory)}
        </div>
        <span className="text-xs font-bold text-slate-800 text-center line-clamp-2 px-1">
          {entry.subjectName || entry.label || entry.eventName}
        </span>
        {isCancellingEvent && (
          <div className="mt-1.5">
            <span className="text-[9px] font-extrabold text-red-600 bg-red-100 px-2 py-0.5 rounded-full uppercase tracking-wider">
              Suspended Hours
            </span>
          </div>
        )}
        {!isCancellingEvent && (
          <div className="mt-1.5">
            <span className="text-[9px] font-bold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full uppercase tracking-wider">
              Academic Block
            </span>
          </div>
        )}
      </div>
    );
  };

  const getPrioritizedEntries = (dayEntries) => {
    if (!dayEntries || dayEntries.length === 0) return [];
    const overrides = dayEntries.filter(entry => entry.isOverride === true);
    const regularEntries = dayEntries.filter(entry => !entry.isOverride);
    return [...overrides, ...regularEntries];
  };

  const buildTimeSlots = useCallback(() => {
    let timeBoundaries = new Set();
    weekData?.forEach(day => {
      (day.entries || []).forEach(entry => {
        if (entry.timeSlot && entry.timeSlot.start && entry.timeSlot.end) {
          timeBoundaries.add(entry.timeSlot.start);
          timeBoundaries.add(entry.timeSlot.end);
        }
      });
    });
    
    const sortedTimes = Array.from(timeBoundaries).sort((a, b) => {
      const parse = t => {
        const m = t.match(/(\d{1,2}):(\d{2}) ?([AP]M)?/i);
        if (!m) return t;
        let h = parseInt(m[1], 10);
        const min = parseInt(m[2], 10);
        if (m[3]) {
          const ampm = m[3].toUpperCase();
          if (ampm === 'PM' && h !== 12) h += 12;
          if (ampm === 'AM' && h === 12) h = 0;
        }
        return h * 60 + min;
      };
      return parse(a) - parse(b);
    });
    
    const allSlots = [];
    for (let i = 0; i < sortedTimes.length - 1; i++) {
      allSlots.push({
        key: `${sortedTimes[i]}-${sortedTimes[i+1]}`,
        start: sortedTimes[i],
        end: sortedTimes[i+1]
      });
    }
    return allSlots;
  }, [weekData]);

  const buildDaySlotMap = useCallback((dayEntries, allSlots) => {
    const map = [];
    let i = 0;
    const prioritizedEntries = getPrioritizedEntries(dayEntries);
    
    while (i < allSlots.length) {
      const slot = allSlots[i];
      const entry = prioritizedEntries.find(e =>
        e.timeSlot &&
        e.timeSlot.start <= slot.start &&
        e.timeSlot.end >= slot.end
      );
      
      if (entry) {
        let span = 1;
        let j = i + 1;
        while (j < allSlots.length && entry.timeSlot.start <= allSlots[j].start && entry.timeSlot.end >= allSlots[j].end) {
          span++;
          j++;
        }
        map.push({ entry, span });
        i += span;
      } else {
        map.push({ entry: null, span: 1 });
        i++;
      }
    }
    return map;
  }, []);

  const CurrentClassIndicator = () => {
    const currentClass = getCurrentClass();
    const nextClass = getNextClass();

    if (!currentClass && !nextClass) return null;

    return (
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 rounded-2xl p-5 text-white mb-6 shadow-md border border-indigo-400/20 mx-4 mt-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3.5">
            <div className="bg-white/15 p-3 rounded-xl backdrop-blur-md ring-1 ring-white/10 animate-pulse">
              {currentClass ? (
                <PlayCircle className="w-6 h-6 text-white" />
              ) : (
                <Clock className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h3 className="font-bold text-lg tracking-tight">
                {currentClass ? 'Currently Active Session' : 'Next Scheduled Hour'}
              </h3>
              <p className="text-indigo-100 text-xs font-medium">
                {currentClass ? 'Live execution matrix tracker' : 'Upcoming programmatic module block'}
              </p>
            </div>
          </div>
          
          <div className="text-right bg-black/10 border border-white/5 px-4 py-2.5 rounded-xl backdrop-blur-md">
            <div className="font-bold text-base text-white tracking-tight">
              {currentClass ? currentClass.subjectName : nextClass?.subjectName}
            </div>
            {currentClass?.subjectCode && (
              <div className="text-indigo-200 text-[10px] font-mono font-bold mt-0.5 tracking-wider">{currentClass.subjectCode}</div>
            )}
            <div className="text-indigo-100 text-xs font-semibold mt-0.5">
              {currentClass?.className || nextClass?.className}
            </div>
            <div className="text-indigo-200 text-[11px] font-bold mt-1 inline-flex items-center gap-1 bg-white/10 px-2 py-0.5 rounded-md">
              <Clock className="w-3 h-3" /> {currentClass ? getTimeDisplay(currentClass.timeSlot) : nextClass ? getTimeDisplay(nextClass.timeSlot) : ''}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ViewSelector = () => {
    const totalSessions = calculateTotalWeekSessions();
    
    const viewOptions = [
      { id: 'master', label: 'Master View', icon: LayoutGrid, description: 'Original schedule without events' },
      { id: 'week', label: 'Week View', icon: CalendarDays, description: 'Complete schedule with events & overrides' },
      { id: 'day', label: 'Day View', icon: Calendar, description: 'Detailed daily schedule' }
    ];

    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-6 overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-slate-800 tracking-tight">
                Academic Timetable Matrix
              </h1>
              <p className="text-slate-400 text-xs font-medium mt-1">
                {weekData && weekData.length > 0 && 
                  `${moment(weekData[0].dateISO).format('DD MMMM YYYY')} — ${moment(weekData[6].dateISO).format('DD MMMM YYYY')}`
                }
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="bg-slate-100 rounded-xl p-1 flex gap-1">
                {viewOptions.map(option => (
                  <button
                    key={option.id}
                    onClick={() => handleViewChange(option.id)}
                    className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 group ${
                      viewType === option.id 
                        ? 'bg-white text-indigo-600 shadow-sm' 
                        : 'text-slate-500 hover:bg-slate-200/50 hover:text-slate-800'
                    }`}
                    title={option.description}
                  >
                    <option.icon className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{option.label}</span>
                  </button>
                ))}
              </div>
              
              {viewType !== 'day' && (
                <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
                  <button
                    onClick={prevWeek}
                    className="p-1.5 hover:bg-white text-slate-500 hover:text-slate-700 rounded-lg transition-all"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={todayWeek}
                    className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-white rounded-lg transition-all"
                  >
                    Today
                  </button>
                  <button
                    onClick={nextWeek}
                    className="p-1.5 hover:bg-white text-slate-500 hover:text-slate-700 rounded-lg transition-all"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="px-6 py-3 bg-slate-50/50 flex flex-wrap items-center justify-between gap-4 border-t border-slate-100">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 shadow-sm">
                <BookOpen className="w-4 h-4" />
              </div>
              <div>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Teaching Blocks</div>
                <div className="text-base font-black text-slate-800 leading-none mt-0.5">{totalSessions}</div>
              </div>
            </div>
            <div className="w-px h-8 bg-slate-200"></div>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600 shadow-sm">
                <Layers className="w-4 h-4" />
              </div>
              <div>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Current Grid Scope</div>
                <div className="text-xs font-bold text-slate-700 capitalize mt-0.5">{viewType} Profile</div>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-3 text-[11px] font-medium text-slate-500">
            <span className="font-bold text-slate-400 uppercase text-[10px] tracking-wider">Legend:</span>
            <div className="flex items-center gap-1.5 bg-white border border-slate-100 px-2 py-0.5 rounded-lg shadow-sm">
              <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
              <span>Modified</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white border border-slate-100 px-2 py-0.5 rounded-lg shadow-sm">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              <span>Lab</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white border border-slate-100 px-2 py-0.5 rounded-lg shadow-sm">
              <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
              <span>Break</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white border border-slate-100 px-2 py-0.5 rounded-lg shadow-sm">
              <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
              <span>Theory</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white border border-slate-100 px-2 py-0.5 rounded-lg shadow-sm">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span>Holiday/Exam</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white border border-slate-100 px-2 py-0.5 rounded-lg shadow-sm">
              <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
              <span>Event</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // MASTER VIEW - Show ALL teaching sessions, NO events, NO cancellation
  const MasterView = () => {
    const allSlots = buildTimeSlots();
    // Only show Sunday if it has sessions
    const daysOfWeek = (weekData?.filter(day => {
      if (day.dayName === 'Sunday') {
        const entries = (day.entries || []).filter(e => !e.isEvent && !e.isOverride);
        const teachingSessions = entries.filter(e => {
          if (e.isBreak) return false;
          if (e.type === 'Custom' && !e.isTeachingSession) return false;
          return e.isTeachingSession !== false;
        }).length;
        return teachingSessions > 0;
      }
      return true;
    }) || []).map(day => {
      const entries = (day.entries || []).filter(e => !e.isEvent && !e.isOverride);
      const teachingSessions = entries.filter(e => {
        if (e.isBreak) return false;
        if (e.type === 'Custom' && !e.isTeachingSession) return false;
        return e.isTeachingSession !== false;
      }).length;
      return {
        dateISO: day.dateISO,
        dayName: day.dayName,
        isToday: day.isToday,
        entries,
        teachingSessions
      };
    });

    if (allSlots.length === 0) {
      return (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <CalendarDays className="w-8 h-8 text-slate-300" />
          </div>
          <h3 className="text-base font-bold text-slate-800 mb-1">No Operational Blueprint</h3>
          <p className="text-slate-400 text-sm">No primary instruction blocks are mapped out for this calendar cycle week.</p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="sticky left-0性能 z-10 bg-slate-50 px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wider text-slate-500 border-r border-slate-100 min-w-[130px]">
                  Academic Day
                </th>
                {allSlots.map((slot, idx) => (
                  <th key={idx} className="px-4 py-3.5 text-center text-[11px] font-mono font-bold text-slate-400 bg-slate-50 border-r border-slate-100 min-w-[140px]">
                    {format12Hour(slot.start)} - {format12Hour(slot.end)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {daysOfWeek.map(day => {
                const slotMap = buildDaySlotMap(day.entries, allSlots);
                let cellIdx = 0;
                return (
                  <tr key={day.dateISO} className="border-b border-slate-100 hover:bg-slate-50/20 transition-colors">
                    <td 
                      className={`sticky left-0 z-10 px-5 py-4 border-r border-slate-100 cursor-pointer transition-colors ${
                        day.isToday ? 'bg-indigo-50/50 text-indigo-600 font-bold' : 'bg-white text-slate-700 hover:bg-indigo-50/20'
                      }`}
                      onClick={() => handleDateSelect(day.dateISO)}
                    >
                      <div className="font-bold text-sm">{day.dayName}</div>
                      <div className="text-[10px] text-slate-400 font-medium mt-1">{day.teachingSessions} operational block{day.teachingSessions !== 1 ? 's' : ''}</div>
                    </td>
                    {slotMap.map(({ entry }, idx) => {
                      if (entry === null) {
                        return <td key={cellIdx++} className="px-2 py-3 text-center text-slate-300 font-mono text-xs bg-slate-50/10 border-r border-slate-100 font-light">—</td>;
                      }
                      if (entry.isBreak) {
                        return (
                          <td key={cellIdx++} className="px-2 py-3 text-center bg-amber-50/20 align-middle border-r border-slate-100">
                            <div className="flex flex-col items-center justify-center gap-0.5">
                              <Coffee className="w-3.5 h-3.5 text-amber-500" />
                              <span className="text-[11px] font-bold text-amber-700">{entry.label || 'Break'}</span>
                            </div>
                          </td>
                        );
                      }
                      return (
                        <td
                          key={cellIdx++}
                          className={`px-2 py-3 align-middle transition-all duration-200 border-r border-slate-100 ${
                            entry.type === 'Lab' ? 'bg-purple-50/15' : 'bg-white'
                          }`}
                          onMouseEnter={() => setHoveredCell(`${day.dateISO}-${idx}`)}
                          onMouseLeave={() => setHoveredCell(null)}
                        >
                          {renderTeachingCell(entry, hoveredCell === `${day.dateISO}-${idx}`)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // WEEK VIEW - Shows teaching sessions + events as separate cells/badges
  const WeekView = () => {
    const allSlots = buildTimeSlots();
    // Only show Sunday if it has sessions
    const daysOfWeek = (weekData?.filter(day => {
      if (day.dayName === 'Sunday') {
        const hasOverride = (day.entries || []).some(e => e.isOverride);
        const entries = hasOverride
          ? (day.entries || []).filter(e => e.isOverride)
          : (day.entries || []).filter(e => !e.isEvent);
        const teachingSessions = calculateTotalTeachingSessions(entries, false);
        return teachingSessions > 0;
      }
      return true;
    }) || []).map(day => {
      const hasCancellingEvent = day.hasCancellingEvent || false;
      const hasOverride = (day.entries || []).some(e => e.isOverride);
      let entries;
      if (hasCancellingEvent) {
        entries = (day.entries || []).filter(e => e.isEvent);
      } else if (hasOverride) {
        entries = (day.entries || []).filter(e => e.isOverride);
      } else {
        entries = (day.entries || []);
      }
      return {
        dateISO: day.dateISO,
        dayName: day.dayName,
        isToday: day.isToday,
        entries,
        hasOverrides: hasOverride,
        hasCancellingEvent,
        nonCancellingEvents: getNonCancellingEventsForDay(day),
        cancellingEvents: getCancellingEventsForDay(day),
        teachingSessions: hasCancellingEvent ? 0 : calculateTotalTeachingSessions(entries, false)
      };
    });

    if (allSlots.length === 0) {
      return (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <CalendarDays className="w-8 h-8 text-slate-300" />
          </div>
          <h3 className="text-base font-bold text-slate-800 mb-1">No Track Metrics Mapped</h3>
          <p className="text-slate-400 text-sm">No formal sessions or system scheduling configurations recorded for this period block.</p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <CurrentClassIndicator />
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="sticky left-0 z-10 bg-slate-50 px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wider text-slate-500 border-r border-slate-100 min-w-[130px]">
                  Academic Day
                </th>
                {allSlots.map((slot, idx) => (
                  <th key={idx} className="px-4 py-3.5 text-center text-[11px] font-mono font-bold text-slate-400 bg-slate-50 border-r border-slate-100 min-w-[140px]">
                    {format12Hour(slot.start)} - {format12Hour(slot.end)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {daysOfWeek.map(day => {
                const slotMap = buildDaySlotMap(day.entries, allSlots);
                let cellIdx = 0;
                
                if (day.hasCancellingEvent && day.cancellingEvents.length > 0) {
                  const event = day.cancellingEvents[0];
                  return (
                    <React.Fragment key={day.dateISO + '-container'}>
                      <tr key={day.dateISO + '-event'} className="border-b border-slate-100 bg-red-50/5 hover:bg-red-50/10 transition-colors">
                        <td
                          className="sticky left-0 z-10 px-5 py-4 border-r border-slate-100 cursor-pointer bg-red-50/40 text-red-700 align-middle"
                          onClick={() => handleDateSelect(day.dateISO)}
                        >
                          <div className="font-bold text-sm text-red-900">{day.dayName}</div>
                          <div className="text-[10px] text-red-500/70 font-semibold mt-0.5">{moment(day.dateISO).format('DD MMM')}</div>
                        </td>
                        <td colSpan={allSlots.length} className="px-6 py-4 border-r border-slate-100 text-center">
                          {renderEventCell(event)}
                        </td>
                      </tr>
                      <tr key={day.dateISO + '-placeholder'} style={{height: 0, padding: 0, border: 0}}>
                        <td colSpan={allSlots.length + 1} style={{height: 0, padding: 0, border: 0}}></td>
                      </tr>
                    </React.Fragment>
                  );
                }

                return (
                  <tr key={day.dateISO} className="border-b border-slate-100 hover:bg-slate-50/20 transition-colors">
                    <td
                      className={`sticky left-0 z-10 px-5 py-4 border-r border-slate-100 cursor-pointer transition-colors ${
                        day.isToday ? 'bg-indigo-50/40 text-indigo-600 font-bold' : 'bg-white text-slate-700 hover:bg-indigo-50/20'
                      }`}
                      onClick={() => handleDateSelect(day.dateISO)}
                    >
                      <div className="font-bold text-sm">{day.dayName}</div>
                      <div className="text-[10px] text-slate-400 font-semibold">{moment(day.dateISO).format('DD MMM')}</div>
                      <div className="text-[10px] font-bold text-slate-500 mt-1">{day.teachingSessions} block{day.teachingSessions !== 1 ? 's' : ''}</div>
                      {day.hasOverrides && (
                        <div className="mt-1">
                          <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-md uppercase tracking-wide">Modified</span>
                        </div>
                      )}
                      {day.nonCancellingEvents.length > 0 && (
                        <div className="mt-1.5 flex flex-col gap-0.5">
                          <span className="text-[9px] font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-md truncate max-w-[110px]" title={day.nonCancellingEvents[0].eventName || day.nonCancellingEvents[0].subjectName}>
                            {day.nonCancellingEvents[0].eventName || day.nonCancellingEvents[0].subjectName || day.nonCancellingEvents[0].label || 'Event'}
                          </span>
                        </div>
                      )}
                    </td>
                    {slotMap.map(({ entry }, idx) => {
                      if (entry === null) {
                        return <td key={cellIdx++} className="px-2 py-3 text-center text-slate-300 font-mono text-xs bg-slate-50/10 border-r border-slate-100 font-light">—</td>;
                      }
                      if (entry.isEvent) {
                        return <td key={cellIdx++} className="px-2 py-3 text-center text-indigo-400 font-mono text-xs bg-indigo-50/20 border-r border-slate-100 font-medium">Event</td>;
                      }
                      return (
                        <td
                          key={cellIdx++}
                          className={`px-2 py-3 align-middle border-r border-slate-100 transition-all duration-200 ${
                            entry.isBreak ? 'bg-amber-50/10' : 
                            entry.isOverride ? 'bg-gradient-to-br from-amber-50/40 via-white to-amber-50/10' :
                            entry.type === 'Lab' ? 'bg-purple-50/15' : 'bg-white'
                          } hover:bg-slate-50`}
                          onMouseEnter={() => setHoveredCell(`${day.dateISO}-${idx}`)}
                          onMouseLeave={() => setHoveredCell(null)}
                        >
                          {renderTeachingCell(entry, hoveredCell === `${day.dateISO}-${idx}`)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // DAY VIEW - Shows teaching sessions + events in separate sections
  const DayView = () => {
    const selectedDayData = getSelectedDay();
    
    const availableDates = weekData?.map(day => ({
      value: day.dateISO,
      label: `${day.dayName}, ${moment(day.dateISO).format('DD MMMM YYYY')}`,
      isToday: day.isToday,
      hasEvent: (day.calendarEvents?.length > 0) || (day.entries?.some(e => e.isEvent) || false),
      isCancelled: day.hasCancellingEvent
    })) || [];

    const handleDateChange = (e) => {
      const newDate = e.target.value;
      setSelectedDate(newDate);
    };

    if (!selectedDayData && weekData && weekData.length > 0 && !selectedDate) {
      const today = weekData.find(d => d.isToday);
      const firstDayWithSessions = weekData.find(d => calculateTotalTeachingSessions(d.entries, false) > 0);
      if (today) {
        setSelectedDate(today.dateISO);
      } else if (firstDayWithSessions) {
        setSelectedDate(firstDayWithSessions.dateISO);
      } else if (weekData[0]) {
        setSelectedDate(weekData[0].dateISO);
      }
      return null;
    }

    if (!selectedDayData) {
      return (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-slate-300" />
          </div>
          <h3 className="text-base font-bold text-slate-800 mb-1">Index Missing Mapping</h3>
          <p className="text-slate-400 text-sm">Please pick an available calendar structural entry context from selection options.</p>
        </div>
      );
    }

    const allSlots = buildTimeSlots();
    const hasCancellingEvent = selectedDayData.hasCancellingEvent;
    const hasOverride = (selectedDayData.entries || []).some(e => e.isOverride);
    
    let filteredEntries;
    if (hasCancellingEvent) {
      filteredEntries = (selectedDayData.entries || []).filter(entry => entry.isEvent);
    } else if (hasOverride) {
      filteredEntries = (selectedDayData.entries || []).filter(entry => entry.isOverride);
    } else {
      filteredEntries = (selectedDayData.entries || []);
    }
    const allEvents = filteredEntries.filter(entry => entry.isEvent);
    const cancellingEvents = allEvents.filter(e => shouldCancelClasses(e));
    const nonCancellingEvents = allEvents.filter(e => !shouldCancelClasses(e));
    const teachingEntries = filteredEntries.filter(entry => !entry.isEvent && !entry.isBreak);

    if (allSlots.length === 0 && !hasCancellingEvent && nonCancellingEvents.length === 0) {
      return (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-slate-300" />
          </div>
          <h3 className="text-base font-bold text-slate-800 mb-1">No Blocks Programmed</h3>
          <p className="text-slate-400 text-sm">No teaching structures are active for {selectedDayData.dayName}.</p>
        </div>
      );
    }

    const slotMap = allSlots.length > 0 ? buildDaySlotMap(filteredEntries, allSlots) : [];
    const hasOverrides = filteredEntries.some(e => e.isOverride) || false;
    const teachingSessions = hasCancellingEvent ? 0 : calculateTotalTeachingSessions(filteredEntries, false);

    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <select
                  value={selectedDate || selectedDayData.dateISO}
                  onChange={handleDateChange}
                  className="appearance-none pl-4 pr-10 py-2.5 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent cursor-pointer hover:border-slate-300 shadow-sm transition-all"
                >
                  {availableDates.map(date => (
                    <option key={date.value} value={date.value}>
                      {date.label} {date.isToday ? '(Today)' : ''} {date.hasEvent ? '📅' : ''} {date.isCancelled ? '🚫' : ''}
                    </option>
                  ))}
                </select>
                <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>
            <div className="flex items-center gap-4">
              {hasCancellingEvent && (
                <div className="px-3 py-1.5 bg-red-50 text-red-700 text-xs font-bold rounded-xl flex items-center gap-1.5 border border-red-100">
                  <XCircle className="w-4 h-4" />
                  <span>Instruction Suspended</span>
                </div>
              )}
              <div className="text-right">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Day Teaching Count</div>
                <div className="text-2xl font-black text-slate-800 leading-none mt-0.5">{teachingSessions}</div>
              </div>
              {hasOverrides && (
                <div className="px-3 py-1.5 bg-amber-50 text-amber-700 text-xs font-bold rounded-xl flex items-center gap-1.5 border border-amber-100">
                  <AlertCircle className="w-4 h-4" />
                  <span>Modified Cycle</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <CurrentClassIndicator />
        
        {/* Show cancelling events (holidays/exams) */}
        {cancellingEvents.length > 0 && (
          <div className="px-6 py-5 bg-gradient-to-r from-red-50/30 to-white border-b border-red-100">
            <div className="flex items-center gap-2 mb-3.5">
              <div className="w-1 h-4 bg-red-500 rounded-full"></div>
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Holidays & Examinations Matrix</h3>
            </div>
            <div className="grid gap-3">
              {cancellingEvents.map((event, idx) => (
                <div key={idx} className="bg-white border border-red-100 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1 bg-slate-50 rounded-md">
                        {getEventIcon(event.eventType, event.eventCategory)}
                      </div>
                      <div className="font-bold text-slate-800 text-sm">
                        {event.subjectName || event.label || event.eventName}
                      </div>
                    </div>
                    <div className="text-slate-500 text-xs font-medium">
                      {event.eventType === 'holiday' ? 'Official Holiday Block' : event.eventType === 'exam' ? 'Formal Examination Cycle' : 'Academic Event'} • 
                      {event.isAllDay ? ' Full Cycle Block' : ` ${getTimeDisplay(event.timeSlot)}`}
                    </div>
                    {event.description && (
                      <div className="text-slate-600 text-xs mt-2 bg-slate-50 p-2 rounded-lg">
                        {event.description}
                      </div>
                    )}
                  </div>
                  <div className="px-2.5 py-1 bg-red-50 text-red-600 text-[10px] rounded-lg font-bold uppercase border border-red-100 self-start sm:self-center">
                    Classes Suspended
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Show non-cancelling events (academic events) */}
        {nonCancellingEvents.length > 0 && (
          <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-indigo-50/20 to-white">
            <div className="flex items-center gap-2 mb-3.5">
              <div className="w-1 h-4 bg-indigo-500 rounded-full"></div>
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Institutional Engagements</h3>
              <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-bold rounded-full">
                {nonCancellingEvents.length} Active
              </span>
            </div>
            <div className="grid gap-3">
              {nonCancellingEvents.map((event, idx) => (
                <div key={idx} className="bg-white border border-indigo-50 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1 bg-slate-50 rounded-md">
                        {getEventIcon(event.eventType, event.eventCategory)}
                      </div>
                      <div className="font-bold text-slate-800 text-sm">
                        {event.subjectName || event.label || event.eventName}
                      </div>
                    </div>
                    <div className="text-slate-500 text-xs font-medium">
                      {event.eventType || 'Event'} • {event.isAllDay ? 'Full Operational Block' : getTimeDisplay(event.timeSlot)}
                    </div>
                    {event.description && (
                      <div className="text-slate-600 text-xs mt-2 bg-slate-50 p-2 rounded-lg">
                        {event.description}
                      </div>
                    )}
                  </div>
                  <div className="px-2.5 py-1 bg-emerald-50 text-emerald-600 text-[10px] rounded-lg font-bold uppercase border border-emerald-100 self-start sm:self-center">
                    Classes Unaffected
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Show teaching sessions only if no cancelling events */}
        {!hasCancellingEvent && teachingEntries.length > 0 && (
          <div className="divide-y divide-slate-100">
            {slotMap.map(({ entry }, idx) => {
              if (entry === null) {
                return (
                  <div key={idx} className="px-6 py-4 flex items-center gap-4 bg-slate-50/20">
                    <div className="w-28 flex-shrink-0 text-xs font-mono font-bold text-slate-300">—</div>
                    <div className="flex-1 text-xs font-medium text-slate-400">No session scheduled for this bracket</div>
                  </div>
                );
              }
              if (entry.isEvent || entry.isBreak) return null;
              
              const timeSlot = entry.timeSlot;
              const isLive = isCurrentLiveSession(entry);
              return (
                <div 
                  key={idx} 
                  className={`px-6 py-4 flex flex-col md:flex-row md:items-center gap-4 transition-all hover:bg-slate-50/30 ${
                    entry.isOverride ? 'bg-gradient-to-r from-amber-50/20 to-white border-l-4 border-amber-400' :
                    entry.type === 'Lab' ? 'bg-purple-50/5' : 'bg-white'
                  } ${isLive ? 'ring-2 ring-indigo-600 ring-offset-2 bg-indigo-50/40 shadow-md rounded-xl mx-2 my-1' : ''}`}
                >
                  <div className="md:w-28 flex-shrink-0">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 rounded-lg text-slate-600">
                      <Clock className="w-3.5 h-3.5" />
                      <span className="font-mono text-xs font-bold">
                        {getTimeDisplay(timeSlot)}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-800 text-base tracking-tight">
                          {entry.subjectName || entry.className || 'Session'}
                        </span>
                        {entry.subjectCode && (
                          <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-mono font-bold rounded">
                            {entry.subjectCode}
                          </span>
                        )}
                        {entry.type === 'Lab' && (
                          <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-bold rounded flex items-center gap-1">
                            <FlaskConical className="w-3 h-3" /> Lab Module
                          </span>
                        )}
                        {entry.isOverride && (
                          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" /> Modified
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-slate-500 font-medium">
                        {entry.className && (
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Users className="w-3.5 h-3.5 text-slate-400" />
                            <span>Batch Code: <strong className="text-slate-700">{entry.className}{entry.section ? ` (${entry.section})` : ''}</strong></span>
                          </div>
                        )}
                        {entry.type === 'Lab' && entry.batchName && (
                          <div className="flex items-center gap-1.5 text-purple-600">
                            <FlaskConical className="w-3.5 h-3.5 text-purple-400" />
                            <span>Lab Partition: <strong className="text-purple-700">{entry.batchName}</strong></span>
                          </div>
                        )}
                        {entry.room && (
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <MapPin className="w-3.5 h-3.5 text-slate-400" />
                            <span>Room Assignment: <strong className="text-slate-700">{entry.room}</strong></span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {!hasCancellingEvent && teachingEntries.length === 0 && nonCancellingEvents.length === 0 && cancellingEvents.length === 0 && (
          <div className="px-6 py-12 text-center bg-slate-50/10">
            <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <Calendar className="w-6 h-6 text-slate-300" />
            </div>
            <p className="text-slate-400 text-sm font-medium">No sessions or events scheduled for this day index.</p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
        <div className="py-20 text-center">
          <div className="relative w-12 h-12 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-slate-100"></div>
            <div className="absolute inset-0 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin"></div>
          </div>
          <div className="text-slate-600 font-bold text-sm">Synchronizing Grid Sync...</div>
          <div className="text-slate-400 text-xs mt-1">Fetching your live schedule matrix parameters</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8">
        <div className="max-w-md mx-auto text-center">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-base font-bold text-slate-800 mb-1">Grid Stream Fault</h3>
          <p className="text-slate-400 text-sm mb-5">{error}</p>
          <button 
            onClick={() => fetchPersonalTimetable(weekStart, selectedDate)}
            className="px-5 py-2 bg-indigo-600 text-white text-xs font-bold rounded-xl hover:bg-indigo-700 shadow-md transition-all"
          >
            Re-Synchronize
          </button>
        </div>
      </div>
    );
  }

  if (!facultyId) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 text-center">
        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <User className="w-8 h-8 text-slate-300" />
        </div>
        <h3 className="text-base font-bold text-slate-800 mb-1">Authentication Context Missing</h3>
        <p className="text-slate-400 text-sm">Please provide an identity parameters instance string reference object.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ViewSelector />
      {viewType === 'master' && <MasterView />}
      {viewType === 'week' && <WeekView />}
      {viewType === 'day' && <DayView />}
    </div>
  );
};

export default Timetable;